import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import { 
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, 
  Link as LinkIcon, Heading1, Heading2, Undo, Redo, Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useEffect } from 'react';

const MenuBar = ({ editor, minimal = false }) => {
  if (!editor) return null;

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);

    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div className="flex flex-wrap gap-1 p-2 border-b border-[#E5E0D5] bg-[#F9F7F2] rounded-t-lg">
      {!minimal && (
        <>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            className={`h-8 w-8 p-0 ${editor.isActive('heading', { level: 1 }) ? 'bg-[var(--liturgical-primary)]/20 text-[var(--liturgical-primary)]' : 'text-[#6B665E]'}`}
          >
            <Heading1 className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            className={`h-8 w-8 p-0 ${editor.isActive('heading', { level: 2 }) ? 'bg-[var(--liturgical-primary)]/20 text-[var(--liturgical-primary)]' : 'text-[#6B665E]'}`}
          >
            <Heading2 className="w-4 h-4" />
          </Button>
          <div className="w-px h-6 bg-[#E5E0D5] mx-1 self-center" />
        </>
      )}
      
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={`h-8 w-8 p-0 ${editor.isActive('bold') ? 'bg-[var(--liturgical-primary)]/20 text-[var(--liturgical-primary)]' : 'text-[#6B665E]'}`}
      >
        <Bold className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={`h-8 w-8 p-0 ${editor.isActive('italic') ? 'bg-[var(--liturgical-primary)]/20 text-[var(--liturgical-primary)]' : 'text-[#6B665E]'}`}
      >
        <Italic className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={`h-8 w-8 p-0 ${editor.isActive('underline') ? 'bg-[var(--liturgical-primary)]/20 text-[var(--liturgical-primary)]' : 'text-[#6B665E]'}`}
      >
        <UnderlineIcon className="w-4 h-4" />
      </Button>
      
      <div className="w-px h-6 bg-[#E5E0D5] mx-1 self-center" />
      
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={`h-8 w-8 p-0 ${editor.isActive('bulletList') ? 'bg-[var(--liturgical-primary)]/20 text-[var(--liturgical-primary)]' : 'text-[#6B665E]'}`}
      >
        <List className="w-4 h-4" />
      </Button>
      
      {!minimal && (
        <>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={`h-8 w-8 p-0 ${editor.isActive('orderedList') ? 'bg-[var(--liturgical-primary)]/20 text-[var(--liturgical-primary)]' : 'text-[#6B665E]'}`}
          >
            <ListOrdered className="w-4 h-4" />
          </Button>
          
          <div className="w-px h-6 bg-[#E5E0D5] mx-1 self-center" />
          
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={setLink}
            className={`h-8 w-8 p-0 ${editor.isActive('link') ? 'bg-[var(--liturgical-primary)]/20 text-[var(--liturgical-primary)]' : 'text-[#6B665E]'}`}
          >
            <LinkIcon className="w-4 h-4" />
          </Button>
        </>
      )}
      
      <div className="flex-1" />
      
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        className="h-8 w-8 p-0 text-[#6B665E]"
      >
        <Undo className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        className="h-8 w-8 p-0 text-[#6B665E]"
      >
        <Redo className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().clearContent().run()}
        className="h-8 w-8 p-0 text-red-500 hover:text-red-600"
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
};

export default function RichTextEditor({ 
  value, 
  onChange, 
  placeholder = "Enter text...",
  minHeight = "150px",
  minimal = false,
  className = ""
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: minimal ? false : { levels: [1, 2, 3] },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-[var(--liturgical-primary)] underline',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: `prose prose-sm max-w-none focus:outline-none font-['Nunito'] text-sm text-[#2D2A26] p-3`,
        style: `min-height: ${minHeight}`,
      },
    },
  });

  // Update content when value changes externally
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '');
    }
  }, [value, editor]);

  return (
    <div className={`rich-text-editor border border-[#E5E0D5] rounded-lg overflow-hidden ${className}`}>
      <style>{`
        .rich-text-editor .ProseMirror {
          min-height: ${minHeight};
          outline: none;
        }
        .rich-text-editor .ProseMirror p {
          margin-bottom: 0.5em;
        }
        .rich-text-editor .ProseMirror ul,
        .rich-text-editor .ProseMirror ol {
          padding-left: 1.5em;
        }
        .rich-text-editor .ProseMirror h1 {
          font-size: 1.5em;
          font-weight: bold;
          margin-bottom: 0.5em;
        }
        .rich-text-editor .ProseMirror h2 {
          font-size: 1.25em;
          font-weight: bold;
          margin-bottom: 0.5em;
        }
        .rich-text-editor .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          color: #9CA3AF;
          float: left;
          height: 0;
          pointer-events: none;
        }
      `}</style>
      <MenuBar editor={editor} minimal={minimal} />
      <EditorContent editor={editor} />
    </div>
  );
}

// Helper to strip HTML for plain text preview
export function stripHtml(html) {
  if (!html) return '';
  if (typeof document === 'undefined') return html.replace(/<[^>]*>/g, '');
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

// Helper to check if content is empty (just whitespace or empty tags)
export function isContentEmpty(html) {
  if (!html) return true;
  const stripped = stripHtml(html).trim();
  return stripped.length === 0;
}
